/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import {
  numericPixels,
  radius,
  ScrollArea,
  spacing,
  stroke,
  Text,
} from "@packages/ui";
import type { KeyboardEvent } from "react";
import { useEffect, useRef } from "react";

import type { TimeValue } from "../../../../lib/utils/hallowAdminDashboard/eventForm.model";
import {
  formatTimeValueLabel,
  timeToMinutes,
} from "../../../../lib/utils/hallowAdminDashboard/events";
import { allQuarterHourTimesInOrder } from "./quarterHourTimes";

/**
 * `height: 100%` is what makes the list scroll instead of overflowing: it gives
 * the flex chain below a definite height to divide up, so the `ScrollArea`
 * viewport ends up shorter than its 96 rows. `minHeight: 0` lets this box shrink
 * below that content height when it is itself a flex item.
 */
const rootCss: Interpolation<ThemeType> = (theme) => ({
  border: `${stroke.regular} solid ${theme.semanticColors.neutralsLowest}`,
  borderRadius: radius.ms,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: spacing.full,
  minHeight: 0,
  overflow: "hidden",
  padding: spacing.xxs,
});

const scrollAreaOuterCss: Interpolation = {
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
};

const viewportCss: Interpolation = {
  height: "100%",
  minHeight: 0,
  paddingInline: spacing.t,
  width: "100%",
};

/** The design stacks rows flush and lets each row's own padding separate them. */
const listCss: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.none,
};

/**
 * `minHeight` is what holds the design's 36px row: `Text type="title" size="s"`
 * has an 18px line height, so the padding and the text only come to 34px.
 */
const optionCss: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  appearance: "none",
  background: "transparent",
  border: "none",
  borderRadius: radius.s,
  color: theme.semanticColors.neutralsHigh,
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  minHeight: numericPixels[36],
  padding: spacing.s,
  textAlign: "center",
  transition: "background-color 100ms ease-out",
  whiteSpace: "nowrap",
  width: "100%",
  "&:hover": {
    backgroundColor: theme.semanticColors.onOverlayLow,
  },
  "&:focus-visible": {
    outline: `${stroke.regular} solid ${theme.semanticColors.primary}`,
    outlineOffset: `-${stroke.regular}`,
  },
});

const optionSelectedCss: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  color: theme.semanticColors.primary,
  "&:hover": {
    backgroundColor: theme.semanticColors.neutralsLowest,
  },
});

/**
 * Mirrors the shared `Calendar`'s disabled-day treatment, so an unreachable time
 * reads as the same state as an unreachable date. Composed after `optionCss` but
 * *before* `optionSelectedCss`, so a row that is both selected and disabled
 * keeps the selected background while still being dimmed.
 */
const optionDisabledCss: Interpolation = {
  cursor: "not-allowed",
  opacity: 0.4,
  "&:hover": {
    backgroundColor: "transparent",
  },
};

const timeKey = (time: TimeValue) => `${time.hour}-${time.minute}-${time.amPm}`;

/** 12:00 AM through 11:45 PM. Fixed, so it is built once. */
const TIME_OPTIONS = allQuarterHourTimesInOrder();

export type TimeQuarterHourListProps = {
  /** Accessible name for the list, e.g. "Start Time". */
  label: string;
  /** Id for the listbox, for a control that edits through it to reference. */
  id?: string;
  selectedTime: TimeValue;
  onSelect: (time: TimeValue) => void;
  /**
   * Whether a time can be picked, e.g. only those after the start. Every
   * quarter hour is rendered either way; the ones this rejects are greyed and
   * inert rather than removed from the list.
   */
  isTimeSelectable?: (time: TimeValue) => boolean;
  /**
   * Scrolls the selection back into view whenever this changes. Pass what makes
   * this a different list — which value is being edited, and on which date —
   * and not the selected time itself: re-centring on a click would slide the
   * list out from under the pointer.
   */
  recenterKey: string;
};

/**
 * The quarter-hour times of a day as an always-visible scrolling list, sitting
 * beside the calendar. Implements the WAI-ARIA listbox pattern with a roving
 * tabindex, so the whole day is reachable with Up/Down/Home/End from a single
 * tab stop rather than 96 of them.
 *
 * All 96 rows are always rendered. Times the caller cannot accept are marked
 * `aria-disabled` and greyed rather than left out of the list: dropping them
 * made the column read as a bug, since a late start left the end column showing
 * a handful of rows above a large empty area. `aria-disabled` rather than the
 * `disabled` attribute keeps those rows announced by screen readers — arrow keys
 * step past them to the next row that can actually be picked.
 */
export function TimeQuarterHourList({
  label,
  id,
  selectedTime,
  onSelect,
  isTimeSelectable,
  recenterKey,
}: TimeQuarterHourListProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /** One flag per rendered row, parallel to `TIME_OPTIONS`. */
  const disabledFlags = TIME_OPTIONS.map((option) =>
    isTimeSelectable ? !isTimeSelectable(option) : false,
  );

  const selectedKey = timeKey(selectedTime);
  /**
   * The selection's row, or for a time between quarter hours the nearest one,
   * which is scrolled to but not highlighted. Rows are 15 minutes apart from
   * midnight, so a row's index is its minutes over 15.
   */
  const scrollAnchorIndex = Math.min(
    TIME_OPTIONS.length - 1,
    Math.round(timeToMinutes(selectedTime) / 15),
  );
  const selectedIndex = selectedTime.minute % 15 === 0 ? scrollAnchorIndex : -1;

  /** The pickable row closest to `index`, the later one on a tie; 0 if none. */
  const nearestSelectableIndex = (index: number) => {
    for (let distance = 0; distance < disabledFlags.length; distance++) {
      if (disabledFlags[index + distance] === false) return index + distance;
      if (disabledFlags[index - distance] === false) return index - distance;
    }
    return 0;
  };

  /**
   * The single tab stop: the selection, or for a time between quarter hours
   * the pickable row nearest it.
   */
  const focusableIndex =
    selectedIndex >= 0
      ? selectedIndex
      : nearestSelectableIndex(scrollAnchorIndex);

  /**
   * The next row at or after `from` in the direction of `step` that can be
   * picked, or -1 when there is none. Keyboard navigation moves between these
   * rather than landing on a disabled row and appearing to do nothing.
   */
  const nextSelectableIndex = (from: number, step: 1 | -1) => {
    for (let i = from; i >= 0 && i < disabledFlags.length; i += step) {
      if (!disabledFlags[i]) return i;
    }
    return -1;
  };

  const focusOptionAt = (index: number) => {
    if (index < 0) return;
    (listRef.current?.children[index] as HTMLElement | undefined)?.focus();
  };

  // Bring the selection, or the row nearest it, into view when the list
  // becomes a different list — on mount, and whenever `recenterKey` changes. The
  // Radix viewport has to be scrolled explicitly, because `scrollIntoView`
  // walks up to the modal's own scroller instead. With all 96 rows always
  // present this matters: the selection is usually well outside the ~7 rows
  // the viewport shows.
  useEffect(() => {
    const viewport = viewportRef.current;
    const option = listRef.current?.querySelector<HTMLElement>(
      "[data-scroll-anchor]",
    );
    if (!viewport || !option) return;
    const viewportBox = viewport.getBoundingClientRect();
    const optionBox = option.getBoundingClientRect();
    const nextTop =
      viewport.scrollTop +
      (optionBox.top - viewportBox.top) -
      viewport.clientHeight / 2 +
      optionBox.height / 2;
    viewport.scrollTop = Math.max(0, nextTop);
  }, [recenterKey]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = Number((event.target as HTMLElement).dataset.timeOptionIndex);
    if (Number.isNaN(index)) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusOptionAt(nextSelectableIndex(index + 1, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        focusOptionAt(nextSelectableIndex(index - 1, -1));
        break;
      case "Home":
        event.preventDefault();
        focusOptionAt(nextSelectableIndex(0, 1));
        break;
      case "End":
        event.preventDefault();
        focusOptionAt(nextSelectableIndex(disabledFlags.length - 1, -1));
        break;
      default:
        break;
    }
  };

  return (
    <div css={rootCss}>
      <div css={scrollAreaOuterCss}>
        <ScrollArea
          ref={viewportRef}
          dependencies={[recenterKey, selectedKey]}
          horizontal={false}
          maskBottom
          maskTop
          masksColor="onOverlayBackground"
          masksSize="xxs"
          showScrollbarWhenScrollable
          viewportCSS={viewportCss}
        >
          <div
            ref={listRef}
            id={id}
            role="listbox"
            aria-label={label}
            css={listCss}
            onKeyDown={handleKeyDown}
          >
            {TIME_OPTIONS.map((option, index) => {
              const selected = index === selectedIndex;
              const disabled = disabledFlags[index];
              return (
                <button
                  key={timeKey(option)}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  // Not the `disabled` attribute: an option the user can see
                  // needs to stay announced and focusable.
                  aria-disabled={disabled || undefined}
                  data-time-option-index={index}
                  data-scroll-anchor={index === scrollAnchorIndex || undefined}
                  tabIndex={index === focusableIndex ? 0 : -1}
                  css={[
                    optionCss,
                    disabled && optionDisabledCss,
                    selected && optionSelectedCss,
                  ]}
                  // Guarded here rather than by omitting the handler, so that
                  // Enter and Space on a disabled row are inert too.
                  onClick={() => {
                    if (!disabled) onSelect(option);
                  }}
                >
                  <Text type="title" size="s" as="span">
                    {formatTimeValueLabel(
                      option.hour,
                      option.minute,
                      option.amPm,
                    )}
                  </Text>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
